import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { TextBoxController } from '@components-clean/core/text-box';
import WidgetIconContainer from '@components-clean/core/widget-icon-container';
import { WidgetIcon } from '@interfaces/widget.interface';
import { FieldDefinitionDto } from '@interfaces-clean/field-rdl-definition.interface';
import { WidgetDataProps } from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/widget-config.interface';
import { FieldDefinitionFieldType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants, { WidgetTypeCode } from '@utils/constants';

interface ListItemProps {
    key: string;
    value: number | string;
    code?: string;
}

interface IIcon {
    key: string;
    value: WidgetIcon;
}

interface WidgetConfigFormProps {
    currentWidget: WidgetDataProps | undefined;
    widgetsData: WidgetDataProps[];
    setWidgetsData: (widgetsArray: WidgetDataProps[]) => void;
    isDisabled: boolean | undefined;
    fieldDefinitionFields: FieldDefinitionDto[];
    widgetType: 'STUDY_DASHBOARD' | 'CONTROLS_HUB';
}

interface FormProps {
    iconName: IIcon | null;
    displayName: string;
    typeCode: ListItemProps | null;
    tableCode: ListItemProps | null;
    fieldKey: ListItemProps | null;
    fieldName: string | null;
    useOnSiteGrid: boolean;
}

export interface WidgetConfigFormElement {
    runFormValidation: () => Promise<boolean>;
}

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const WidgetConfigForm = forwardRef<WidgetConfigFormElement, WidgetConfigFormProps>(
    ({ currentWidget, widgetsData, setWidgetsData, isDisabled, fieldDefinitionFields, widgetType }, ref) => {
        const { t } = useTranslation();
        const {
            DataSourceLOV,
            widgets: { IconsList },
        } = Constants;

        const [fieldKeyLOV, setFieldKeyLOV] = useState<ListItemProps[]>([]);

        const typesLOV = [
            {
                key: t('admin:STATIC'),
                value: WidgetTypeCode.Static,
            },
            {
                key: t('common:Calculated'),
                value: WidgetTypeCode.Calculated,
            },
        ];

        const { control, trigger, reset, resetField, watch, setValue } = useForm<FormProps>({
            mode: 'all',
            defaultValues: {
                iconName: null,
                typeCode: null,
                tableCode: null,
                fieldKey: null,
                fieldName: null,
                useOnSiteGrid: false,
            },
        });

        const runFormValidation = () => {
            return trigger();
        };

        useImperativeHandle(ref, () => ({
            runFormValidation,
        }));

        useEffect(() => {
            if (currentWidget) {
                const currentIcon = IconsList.find((iconItem) => iconItem.value === currentWidget?.iconName);
                const currentTypeCode = typesLOV.find((type) => type.value === currentWidget?.typeCode);
                const currentTableCode = DataSourceLOV.find(
                    (dataSource) => dataSource.value === currentWidget?.tableCode,
                );

                reset({
                    iconName: currentIcon,
                    displayName: currentWidget?.displayName || '',
                    typeCode: currentTypeCode,
                    tableCode: currentTableCode,
                    useOnSiteGrid: currentWidget?.useOnSiteGrid ?? false,
                });
            }
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [currentWidget]);

        useEffect(() => {
            const currentFieldKey = fieldDefinitionFields.find((field) => field?.key === currentWidget?.fieldKey);
            setValue(
                'fieldKey',
                currentFieldKey
                    ? {
                          key: currentFieldKey.name,
                          value: currentFieldKey.key,
                      }
                    : null,
            );
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [currentWidget]);

        const watchTableCode = watch('tableCode');
        const watchTypeCode = watch('typeCode')?.value;

        useEffect(() => {
            const filteredFieldKeys = fieldDefinitionFields
                .filter(
                    (field) =>
                        field.isCalculated === (watchTypeCode === 2) &&
                        field.dataType === watchTableCode?.code &&
                        (field.fieldType === FieldDefinitionFieldType.INT ||
                            field.fieldType === FieldDefinitionFieldType.FLOAT),
                )
                .map((item) => ({
                    key: item.name,
                    value: item.key,
                }));

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFieldKeyLOV([...filteredFieldKeys] as ListItemProps[]);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [watchTableCode, watchTypeCode]);

        const setValueToState = (value: unknown, field: string, fieldName?: string) => {
            const copyWidgetsData = [...widgetsData];
            const updatedArray = copyWidgetsData.map((item) => {
                if (item.configWidgetId === currentWidget?.configWidgetId) {
                    let resetFields = {}; // Needs for reset values in parent state
                    if (field === 'typeCode') {
                        resetFields = {
                            tableCode: null,
                            fieldKey: '',
                        };
                    } else if (field === 'tableCode') {
                        resetFields = {
                            fieldKey: '',
                            fieldName: '',
                        };
                    }
                    if (field === 'fieldKey') {
                        item.fieldName = fieldName;
                    }
                    return {
                        ...item,
                        ...resetFields,
                        [field]: value,
                    };
                }
                return item;
            });

            setWidgetsData([...updatedArray]);
        };

        if (!currentWidget) return null;

        return (
            <div className="k-display-flex k-align-items-center" style={{ minHeight: '143px' }}>
                <div className={styles.widgetIconWrap}>
                    <WidgetIconContainer
                        icon={widgetsData.find((x) => x.configWidgetId === currentWidget.configWidgetId)?.iconName}
                        isWidgetForm
                    />
                </div>
                <form className="k-w-full k-mr-15" noValidate>
                    <div className="k-display-flex k-grid-cols-3 k-gap-8">
                        <div style={{ width: '33%' }}>
                            <BasicDropDownController
                                control={control}
                                name="iconName"
                                data={IconsList}
                                data-testid="widget-icon"
                                label={`${t('icon')}*`}
                                disabled={isDisabled}
                                textField="key"
                                dataItemKey="value"
                                required
                                handleInputChange={(event) => {
                                    const selectedValue = event.value as IIcon;
                                    setValueToState(selectedValue?.value ?? '', 'iconName');
                                }}
                                filterable
                            />
                        </div>
                        <div style={{ width: '33%' }}>
                            <TextBoxController
                                control={control}
                                name="displayName"
                                data-testid="widget-name"
                                label={`${t('admin:displayName')}*`}
                                disabled={isDisabled}
                                maxLength={30}
                                handleInputChange={(event) => {
                                    setValueToState(event.target.value, 'displayName');
                                }}
                                required
                            />
                        </div>
                        <div style={{ width: '33%' }}>
                            <BasicDropDownController
                                control={control}
                                name="typeCode"
                                data={typesLOV}
                                data-testid="widget-type-code"
                                label={`${t('type')}*`}
                                disabled={isDisabled}
                                textField="key"
                                dataItemKey="value"
                                handleInputChange={(event) => {
                                    const selectedValue = event.value as ListItemProps;

                                    resetField('tableCode', { defaultValue: null });
                                    resetField('fieldKey', { defaultValue: null });

                                    setValueToState(selectedValue?.value ?? null, 'typeCode');
                                    event.target.focus();
                                }}
                                filterable
                                required
                            />
                        </div>
                    </div>
                    <div className="k-display-flex k-grid-cols-3 k-gap-8">
                        <div className="k-display-flex k-align-items-center" style={{ width: '45%' }}>
                            <BasicDropDownController
                                control={control}
                                name="tableCode"
                                data={DataSourceLOV}
                                data-testid="widget-tableCode"
                                label={`${t('dataSource')}*`}
                                disabled={isDisabled || !Boolean(watchTypeCode)}
                                textField="key"
                                dataItemKey="value"
                                required
                                handleInputChange={(event) => {
                                    const selectedValue = event.value as ListItemProps;
                                    resetField('fieldKey', { defaultValue: null });
                                    setValueToState(selectedValue?.value ?? null, 'tableCode');
                                    event.target.focus();
                                }}
                                filterable
                            />
                        </div>
                        <div
                            className="k-display-flex k-align-items-center"
                            style={{ width: widgetType === 'STUDY_DASHBOARD' ? '45%' : '55%' }}
                        >
                            <BasicDropDownController
                                control={control}
                                name="fieldKey"
                                data={fieldKeyLOV}
                                data-testid="widget-fieldName"
                                label={`${t('fieldName')}*`}
                                disabled={isDisabled || !Boolean(watchTableCode)}
                                textField="key"
                                dataItemKey="value"
                                handleInputChange={(event) => {
                                    const selectedValue = event.value as ListItemProps;
                                    setValueToState(selectedValue?.value ?? '', 'fieldKey', selectedValue?.key ?? '');
                                }}
                                required
                                filterable
                            />
                        </div>
                        {widgetType === 'STUDY_DASHBOARD' && (
                            <div className="k-display-flex k-align-items-center" style={{ width: '10%' }}>
                                <span>{t('admin:useOnSiteGrid')}</span>
                                <Controller
                                    control={control}
                                    name="useOnSiteGrid"
                                    render={({ field: { onChange, value } }) => (
                                        <CoreCheckbox
                                            data-testid="use-on-site-grid"
                                            value={value}
                                            onChange={(event) => {
                                                onChange(event.target.value as boolean);
                                                setValueToState(event?.target.value ?? '', 'useOnSiteGrid');
                                            }}
                                            className="k-left-1"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </form>
            </div>
        );
    },
);

export default WidgetConfigForm;
